import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

export interface LoadingProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  text?: string;
  fullPage?: boolean;
}

const Loading = ({ size = 'md', className, text, fullPage = false }: LoadingProps) => {
  const sizes = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-10 w-10',
  };

  const content = (
    <div className={cn('flex flex-col items-center justify-center gap-2', className)}>
      <Loader2 className={cn('animate-spin text-primary-500', sizes[size])} />
      {text && (
        <p className="text-sm text-gray-500 dark:text-secondary-400">{text}</p>
      )}
    </div>
  );

  if (fullPage) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white/80 dark:bg-secondary-950/90 backdrop-blur-sm z-50">
        {content}
      </div>
    );
  }

  return content;
};

// Skeleton loading component
interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
}

const Skeleton = ({ className, variant = 'text' }: SkeletonProps) => {
  const variants = {
    text: 'h-4 w-full rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
  };

  return (
    <div
      className={cn(
        'animate-pulse',
        'bg-gray-200 dark:bg-secondary-800',
        variants[variant],
        className
      )}
    />
  );
};

// Shimmer skeleton with animated gradient
interface ShimmerProps {
  className?: string;
}

const Shimmer = ({ className }: ShimmerProps) => (
  <div
    className={cn(
      'relative overflow-hidden rounded-lg',
      'bg-secondary-800',
      'before:absolute before:inset-0',
      'before:bg-gradient-to-r before:from-transparent before:via-white/[0.05] before:to-transparent',
      'before:animate-shimmer before:bg-[length:200%_100%]',
      className
    )}
  />
);

// Page loading state
const PageLoading = ({ text = 'Loading...' }: { text?: string }) => (
  <div className="flex min-h-[300px] items-center justify-center">
    <Loading size="lg" text={text} />
  </div>
);

// Inline loading for buttons/small areas
const InlineLoading = ({ className }: { className?: string }) => (
  <Loader2 className={cn('h-4 w-4 animate-spin text-current', className)} />
);

// Card skeleton for dashboard
const CardSkeleton = ({ className }: { className?: string }) => (
  <div
    className={cn(
      'rounded-xl p-4',
      'bg-white dark:bg-secondary-900/80',
      'border border-gray-200 dark:border-white/[0.06]',
      className
    )}
  >
    <div className="space-y-3">
      <Skeleton className="h-3 w-1/3" />
      <Skeleton className="h-6 w-1/2" />
      <Skeleton className="h-3 w-1/4" />
    </div>
  </div>
);

// Table row skeleton
const TableRowSkeleton = ({ columns = 4 }: { columns?: number }) => (
  <div className="flex items-center gap-4 py-3 px-4">
    {Array.from({ length: columns }).map((_, i) => (
      <Skeleton
        key={i}
        className={cn('h-4', i === 0 ? 'w-1/4' : 'flex-1')}
      />
    ))}
  </div>
);

export {
  Loading,
  Skeleton,
  Shimmer,
  PageLoading,
  InlineLoading,
  CardSkeleton,
  TableRowSkeleton,
};
